/**
 * MobileStyleRail — iter-104 — single-row horizontal Style Pack picker.
 *
 * Free (7): cinematic · minimal · romantic · bold_gradient · faith
 *           + love_letter · pen_paper (NEW · iter-104)
 * Paid (9): vintage · brutalist · polaroid · watercolor · playful · luxury
 *           + vow · forever · scrapbook (NEW · iter-104)
 *
 * Locked tiles show a Lock badge + a "Plus · $7.99/mo" ribbon so the
 * upgrade price is visible BEFORE the user invests a tap.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { STYLE_PACKS, isStylePackV2, setRecipeOverride } from "../../lib/stylePacks";
import { tap } from "../../lib/haptic";
import { track } from "../../lib/track";
import MarqueeRail from "../MarqueeRail";

const FREE_PACK_IDS = [
    "cinematic", "minimal", "romantic", "bold_gradient", "faith",
    "love_letter", "pen_paper",
];

// Display order — free first, then paid. New letter-family packs are
// surfaced right after the cinematic / minimal staples since they're the
// most emotionally distinctive new additions.
const DISPLAY_ORDER = [
    "cinematic", "minimal", "romantic", "love_letter", "pen_paper",
    "bold_gradient", "faith",
    "vow", "forever", "scrapbook",
    "vintage", "brutalist", "polaroid", "watercolor", "playful", "luxury",
];

// iter-105 — Lazy-load tile background image. Uses IntersectionObserver
// to defer loading until the tile is within 200px of the viewport. Until
// then, we render only the base color from the recipe — image is loaded
// on demand. This cuts initial style-rail weight by ~80KB.
function LazyPackTile({ packDef, variant, isActive, isLocked, packId }) {
    const tileRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (visible) return;
        const el = tileRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            // SSR / very old browsers — render immediately.
            setVisible(true);
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        obs.disconnect();
                        break;
                    }
                }
            },
            { rootMargin: "200px 200px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [visible]);

    const rawBg = variant?.background || "#222";
    // If the recipe references a url(...) image, strip it until visible.
    // Recipes that are pure gradients/solids stay as-is (cheap to paint).
    const hasImage = typeof rawBg === "string" && rawBg.includes("url(");
    const baseBg = hasImage
        ? rawBg.replace(/url\(['"]?[^)]+['"]?\)\s*[^,]*/i, "").replace(/^\s*[,]?\s*/, "") || "#1a1a1a"
        : rawBg;

    return (
        <div
            ref={tileRef}
            className={`relative w-[84px] h-[84px] rounded-2xl overflow-hidden border-2 transition-all ${
                isActive
                    ? "border-amber-300 shadow-[0_0_16px_-2px_rgba(252,211,77,0.55)]"
                    : "border-white/8"
            }`}
            style={{
                background: visible ? rawBg : baseBg,
            }}
            data-testid={`style-rail-tile-bg-${packId}`}
            data-loaded={visible ? "1" : "0"}
        >
            {isLocked && (
                <>
                    <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-amber-300">
                        <Lock className="w-2.5 h-2.5" strokeWidth={2.2} />
                    </span>
                    <span className="absolute left-0 right-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/85 via-black/65 to-transparent flex items-baseline justify-between gap-1">
                        <span className="font-sans text-[8px] uppercase tracking-[0.18em] text-amber-300 font-semibold">
                            Plus
                        </span>
                        <span className="font-serif text-[10px] text-amber-200 italic">
                            $7.99/mo
                        </span>
                    </span>
                </>
            )}
        </div>
    );
}

export default function MobileStyleRail({
    moodId,
    currentPack,
    isPremium = false,
    onChange,
    onPremiumLocked,
}) {
    const handlePick = (packId) => {
        if (!isStylePackV2(packId)) return;
        const packDef = STYLE_PACKS[packId];
        if (!packDef) return;
        const isFreePack = FREE_PACK_IDS.includes(packId);
        if (!isFreePack && !isPremium) {
            tap();
            track("style_rail_locked_tap", { mood_id: moodId, pack: packId });
            if (onPremiumLocked) onPremiumLocked(packId);
            return;
        }
        const free = packDef.variants[0]?.id;
        if (!free) return;
        setRecipeOverride(moodId, `${packId}.${free}`);
        tap();
        track("style_rail_pack_picked", { mood_id: moodId, pack: packId, from: currentPack });
        if (onChange) onChange(packId);
    };

    const freeCount = FREE_PACK_IDS.length;
    const totalCount = DISPLAY_ORDER.length;

    return (
        <section className="mt-5" data-testid="mobile-style-rail">
            <div className="px-4 mb-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-white/55">styles</span>
                </div>
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">
                    {isPremium ? "all unlocked" : `${totalCount} looks · ${freeCount} free`}
                </span>
            </div>

            <MarqueeRail
                className="px-4 -mx-1 pb-1"
                contentClassName="gap-3"
                testId="mobile-style-marquee"
                speed={0.2}
            >
                {DISPLAY_ORDER.map((packId) => {
                    const packDef = STYLE_PACKS[packId];
                    if (!packDef) return null;
                    const variant = packDef.variants[0];
                    const isFree = FREE_PACK_IDS.includes(packId);
                    const isLocked = !isFree && !isPremium;
                    const isActive = currentPack === packId;

                    return (
                        <motion.button
                            key={packId}
                            type="button"
                            whileTap={{ scale: 0.94 }}
                            onClick={() => handlePick(packId)}
                            className={`relative shrink-0 w-[84px] flex flex-col items-center gap-1.5 transition-opacity ${
                                isLocked ? "opacity-90" : ""
                            }`}
                            data-testid={`style-rail-tile-${packId}`}
                        >
                            <LazyPackTile
                                packDef={packDef}
                                variant={variant}
                                isActive={isActive}
                                isLocked={isLocked}
                                packId={packId}
                            />
                            <span className={`text-[10px] tracking-[0.06em] ${
                                isActive ? "text-amber-200" : "text-white/65"
                            }`}>
                                {packDef.label}
                            </span>
                        </motion.button>
                    );
                })}
            </MarqueeRail>
        </section>
    );
}
